'use client';

import { useState, useRef, useEffect } from 'react';
import { Loader2, Play, Upload, User, Mic2, Sparkles, FlaskConical, RefreshCw } from 'lucide-react';

interface Character {
  id: number;
  name: string;
  artwork_path: string | null;
  sampleAudioPath: string | null;
}

interface Result {
  audioPath: string;
  text: string;
  emotion: string;
  characterName: string;
  timestamp: number;
}

const EMOTION_CHIPS = [
  'cold and menacing',
  'laughing',
  'whispering',
  'shouting in anger',
  'deeply sad',
  'excited and breathless',
  'ancient and weary',
  'warm and kind',
  'sarcastic',
  'terrified',
];

export default function VoiceLab({ characters }: { characters: Character[] }) {
  const [serviceStatus, setServiceStatus] = useState<'checking' | 'online' | 'loading' | 'offline' | 'ready'>('checking');
  const [selectedChar, setSelectedChar] = useState<Character | null>(null);
  const [uploadedRef, setUploadedRef]   = useState<File | null>(null);
  const [uploadedRefUrl, setUploadedRefUrl] = useState<string | null>(null);
  const [text, setText]       = useState('');
  const [emotion, setEmotion] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [results, setResults] = useState<Result[]>([]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Service health check ──
  useEffect(() => {
    async function check() {
      try {
        const res = await fetch('/api/indextts/generate');
        if (!res.ok) { setServiceStatus('offline'); return; }
        const { online, modelLoaded, installed } = await res.json();
        if (online && modelLoaded) setServiceStatus('online');
        else if (online && !modelLoaded) setServiceStatus('loading');
        else if (installed) setServiceStatus('ready');   // installed, VRAM manager will start it
        else setServiceStatus('offline');
      } catch {
        setServiceStatus('offline');
      }
    }
    check();
    const interval = setInterval(check, 15_000);
    return () => clearInterval(interval);
  }, []);

  const hasRef    = selectedChar?.sampleAudioPath || uploadedRef;
  const canGenerate = serviceStatus === 'online' || serviceStatus === 'ready' || serviceStatus === 'loading';

  function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadedRef(file);
    setUploadedRefUrl(URL.createObjectURL(file));
    setSelectedChar(null);
    e.target.value = '';
  }

  async function handleGenerate() {
    if (!text.trim() || !hasRef) return;
    setGenerating(true); setError(null);
    try {
      const fd = new FormData();
      fd.append('text', text.trim());
      fd.append('emotionText', emotion.trim());
      if (uploadedRef) {
        fd.append('referenceAudio', uploadedRef);
      } else if (selectedChar) {
        fd.append('characterId', String(selectedChar.id));
      }

      const res = await fetch('/api/indextts/generate', { method: 'POST', body: fd });
      if (!res.ok) {
        const j = await res.json();
        throw new Error(j.error ?? 'Generation failed');
      }
      const { audioPath } = await res.json();

      setResults(prev => [{
        audioPath,
        text: text.trim(),
        emotion: emotion.trim(),
        characterName: selectedChar?.name ?? 'Custom voice',
        timestamp: Date.now(),
      }, ...prev.slice(0, 9)]);   // keep last 10
    } catch (e: any) { setError(e.message); }
    finally { setGenerating(false); }
  }

  const charName = selectedChar?.name ?? (uploadedRef ? 'Custom voice' : null);

  return (
    <div className="p-8 space-y-8 max-w-5xl">

      {/* Header */}
      <header>
        <div className="flex items-center gap-3 mb-2">
          <FlaskConical size={28} className="text-amber-400" />
          <h2 className="text-4xl font-serif font-bold text-white italic">Testing Grounds</h2>
        </div>
        <p className="text-slate-400 italic">
          Sketch out character voices instantly — pick a voice, describe the mood, and hear it speak.
          No training needed.
        </p>
      </header>

      {/* Service status banner */}
      {serviceStatus === 'ready' && (
        <div className="flex items-center gap-3 text-sm text-slate-300 bg-slate-900 border border-slate-700 rounded-xl px-4 py-3">
          <FlaskConical size={15} className="text-amber-400 shrink-0" />
          <span>IndexTTS-2 will start automatically when you generate — it may take up to 60 seconds on first use while it loads into memory.</span>
        </div>
      )}

      {serviceStatus === 'offline' && (
        <div className="bg-slate-900 border border-amber-500/30 rounded-xl p-5 space-y-3">
          <p className="text-amber-300 font-bold flex items-center gap-2">
            <FlaskConical size={16} /> IndexTTS-2 needs setup
          </p>
          <p className="text-xs text-slate-400 leading-relaxed">
            One-time install — run these in a terminal on Civilbuddy:
          </p>
          <pre className="text-xs bg-slate-950 rounded-lg p-4 text-emerald-300 leading-relaxed overflow-x-auto">{`cd /home/civilbuddy/indextts-service
bash setup.sh

sudo cp indextts-sentinels.service /etc/systemd/system/
sudo systemctl daemon-reload
# Do NOT enable it — the app manages start/stop automatically:
sudo cp sudoers-sentinels-vram /etc/sudoers.d/sentinels-vram`}</pre>
        </div>
      )}

      {serviceStatus === 'loading' && (
        <div className="flex items-center gap-3 text-sm text-amber-300 bg-amber-900/20 border border-amber-500/20 rounded-xl px-4 py-3">
          <Loader2 size={15} className="animate-spin" />
          IndexTTS-2 is warming up — model is loading into GPU memory. Usually takes ~30 seconds…
        </div>
      )}

      {canGenerate && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

          {/* ── Left: controls ── */}
          <div className="space-y-6">

            {/* Voice reference picker */}
            <div className="space-y-3">
              <p className="text-xs font-mono uppercase tracking-widest text-slate-500">Voice Reference</p>
              <p className="text-xs text-slate-400">Pick a character whose designed voice will be cloned, or upload any audio clip.</p>

              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {characters.filter(c => c.sampleAudioPath).map(c => (
                  <button
                    key={c.id}
                    onClick={() => { setSelectedChar(c); setUploadedRef(null); setUploadedRefUrl(null); }}
                    className={`flex flex-col items-center gap-1.5 p-2 rounded-xl border transition-all ${
                      selectedChar?.id === c.id
                        ? 'border-purple-500 bg-purple-900/30'
                        : 'border-slate-700 bg-slate-800/60 hover:border-slate-600'
                    }`}
                  >
                    {c.artwork_path ? (
                      <img src={`/${c.artwork_path}`} alt={c.name} className="w-10 h-10 rounded-lg object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center">
                        <User size={18} className="text-slate-500" />
                      </div>
                    )}
                    <span className="text-[10px] text-slate-300 text-center leading-tight font-bold truncate w-full text-center">{c.name}</span>
                  </button>
                ))}

                {characters.filter(c => !c.sampleAudioPath).map(c => (
                  <div
                    key={c.id}
                    title="No voice designed yet — go to Voice Studio"
                    className="flex flex-col items-center gap-1.5 p-2 rounded-xl border border-slate-800 opacity-30 cursor-not-allowed"
                  >
                    {c.artwork_path ? (
                      <img src={`/${c.artwork_path}`} alt={c.name} className="w-10 h-10 rounded-lg object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center">
                        <User size={18} className="text-slate-600" />
                      </div>
                    )}
                    <span className="text-[10px] text-slate-500 truncate w-full text-center">{c.name}</span>
                  </div>
                ))}
              </div>

              {/* Upload custom reference */}
              <div>
                <input ref={fileInputRef} type="file" accept="audio/*" className="hidden" onChange={handleFileUpload} />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all ${
                    uploadedRef
                      ? 'border-emerald-500 bg-emerald-900/20 text-emerald-300'
                      : 'border-slate-700 bg-slate-800/60 hover:border-slate-600 text-slate-400'
                  }`}
                >
                  <Upload size={14} />
                  {uploadedRef ? `Using: ${uploadedRef.name}` : 'Upload any audio file as reference'}
                </button>
                {uploadedRefUrl && (
                  <audio controls src={uploadedRefUrl} className="w-full h-7 mt-2" />
                )}
              </div>
            </div>

            {/* Text input */}
            <div className="space-y-2">
              <p className="text-xs font-mono uppercase tracking-widest text-slate-500">
                What do they say?
                {charName && <span className="ml-2 text-purple-400 normal-case not-italic">{charName}</span>}
              </p>
              <textarea
                value={text}
                onChange={e => setText(e.target.value)}
                placeholder={selectedChar ? `${selectedChar.name} says…` : 'Type a line here…'}
                rows={3}
                className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-sm text-slate-200 resize-none focus:outline-none focus:border-purple-500 placeholder:text-slate-600"
              />
            </div>

            {/* Emotion input */}
            <div className="space-y-2">
              <p className="text-xs font-mono uppercase tracking-widest text-slate-500">Mood / Emotion <span className="text-slate-600 normal-case">(optional)</span></p>
              <input
                value={emotion}
                onChange={e => setEmotion(e.target.value)}
                placeholder="e.g. cold and menacing, ancient and tired"
                className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-slate-200 focus:outline-none focus:border-purple-500 placeholder:text-slate-600"
              />
              <div className="flex flex-wrap gap-1.5 pt-1">
                {EMOTION_CHIPS.map(chip => (
                  <button
                    key={chip}
                    onClick={() => setEmotion(chip)}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-all ${
                      emotion === chip
                        ? 'border-purple-500 bg-purple-900/40 text-purple-200'
                        : 'border-slate-700 text-slate-400 hover:border-slate-500 hover:text-slate-300'
                    }`}
                  >
                    {chip}
                  </button>
                ))}
              </div>
            </div>

            {error && (
              <p className="text-xs text-red-400 bg-red-900/20 border border-red-500/20 rounded-lg px-3 py-2">{error}</p>
            )}

            <button
              onClick={handleGenerate}
              disabled={generating || !text.trim() || !hasRef}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-amber-600 hover:bg-amber-500 disabled:opacity-40 rounded-xl text-sm font-bold transition-colors"
            >
              {generating
                ? <><Loader2 size={16} className="animate-spin" /> Generating… (10–30 sec)</>
                : <><Sparkles size={16} /> Generate Voice Line</>}
            </button>

            {!hasRef && (
              <p className="text-xs text-slate-600 text-center">Select a character or upload a reference audio file to continue.</p>
            )}
          </div>

          {/* ── Right: results ── */}
          <div className="space-y-4">
            <p className="text-xs font-mono uppercase tracking-widest text-slate-500 flex items-center gap-2">
              <Mic2 size={12} /> Results
            </p>

            {results.length === 0 && !generating && (
              <div className="flex flex-col items-center justify-center h-48 text-slate-700 space-y-2">
                <FlaskConical size={36} />
                <p className="text-sm">Your generations will appear here.</p>
              </div>
            )}

            {generating && (
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex items-center gap-3 text-sm text-slate-400">
                <Loader2 size={14} className="animate-spin text-amber-400 shrink-0" />
                <div>
                  <p className="font-bold text-slate-300">Generating…</p>
                  <p className="text-xs text-slate-500">{charName} · {emotion || 'neutral'}</p>
                </div>
              </div>
            )}

            <div className="space-y-3">
              {results.map((r, i) => (
                <div key={r.timestamp} className={`bg-slate-900 border rounded-xl p-4 space-y-2 ${i === 0 ? 'border-amber-500/30' : 'border-slate-800'}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="space-y-0.5 min-w-0">
                      <p className="text-xs font-bold text-slate-300">{r.characterName}</p>
                      {r.emotion && <p className="text-xs text-amber-400/80 italic">{r.emotion}</p>}
                      <p className="text-xs text-slate-400 line-clamp-2 leading-relaxed">&ldquo;{r.text}&rdquo;</p>
                    </div>
                    <button
                      onClick={() => { setText(r.text); setEmotion(r.emotion); }}
                      title="Reload this setup"
                      className="text-slate-600 hover:text-slate-400 shrink-0 mt-0.5"
                    >
                      <RefreshCw size={13} />
                    </button>
                  </div>
                  <audio controls src={`/${r.audioPath}`} className="w-full h-8" />
                </div>
              ))}
            </div>
          </div>

        </div>
      )}

    </div>
  );
}
